import { cn } from "@/lib/utils";

import type { DocumentStat } from "@/api/queries";

/**
 * A question's forward position in the pipeline, read off its *current* status.
 *
 * `questions` on `DocumentStat` is a snapshot — counts per current status, not a
 * history — so "how many have reached stage N" is reconstructed as "total minus
 * whoever is still sitting at an earlier stage", not read off directly. `failed` gets
 * order 0: a failed row was extracted (the Question row exists), so it still counts
 * there, but we cannot say which later stage it failed *at*, so it stops counting
 * for every stage after that rather than guess. The dedicated "Failed" branch below
 * carries the true total instead.
 */
const STAGE_ORDER: Record<string, number> = {
  extracted: 0,
  failed: 0,
  enriched: 1,
  embedded: 2,
  dedup_checked: 3,
  pending_review: 4,
  stale: 4,
  duplicate: 4,
  approved: 5,
  rejected: 5,
};

function reached(questions: Record<string, number>, stageIndex: number, total: number): number {
  let before = 0;
  for (const [status, n] of Object.entries(questions)) {
    if ((STAGE_ORDER[status] ?? -1) < stageIndex) before += n;
  }
  return Math.max(total - before, 0);
}

type Node = { label: string; done: number; total: number; sub?: string };

/**
 * The automated zone (DESIGN §6/§7: extraction → enrichment → embedding → dedup →
 * clustering) plus the human-review fork it feeds into, and the two branches that
 * can peel off before reaching it — a duplicate folded in at clustering, a failure
 * at any stage along the way.
 */
export function PipelineStepper({
  doc,
}: {
  doc: Pick<DocumentStat, "chunks" | "chunk_total" | "questions" | "question_total">;
}) {
  const { chunks, chunk_total, questions, question_total } = doc;
  const chunksDone = chunk_total - (chunks.raw ?? 0);
  const duplicate = questions.duplicate ?? 0;
  const failed = questions.failed ?? 0;
  const enteredReview = reached(questions, 4, question_total) - duplicate;
  const waitingNow = (questions.pending_review ?? 0) + (questions.stale ?? 0);
  const approved = questions.approved ?? 0;
  const rejected = questions.rejected ?? 0;

  const nodes: Node[] = [
    {
      label: "Ingested",
      done: chunksDone,
      total: chunk_total,
      ...(chunks.failed ? { sub: `${chunks.failed} chunk(s) failed` } : {}),
    },
    {
      label: "Extracted",
      done: question_total,
      total: question_total,
      sub: `${question_total} question${question_total === 1 ? "" : "s"}`,
    },
    { label: "Enriched", done: reached(questions, 1, question_total), total: question_total },
    { label: "Embedded", done: reached(questions, 2, question_total), total: question_total },
    { label: "Deduped", done: reached(questions, 3, question_total), total: question_total },
    { label: "Clustered", done: reached(questions, 4, question_total), total: question_total },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-start">
        {nodes.map((n, i) => (
          <StageNode key={n.label} node={n} first={i === 0} />
        ))}
        <div className="flex w-7 flex-none items-center justify-center pt-[5px]">
          <span className="border-bank-border-strong h-[30px] border-l border-dashed" aria-hidden="true" />
        </div>
        <div className="flex-1 text-center">
          <span className="bg-bank-attention mx-auto mb-2 block size-[15px] rounded-full border-[2.5px] border-bank-attention" />
          <p className="text-sm font-semibold">Pending Review</p>
          <p className="text-bank-muted font-mono text-xs tabular-nums">
            {enteredReview} reached · {waitingNow} waiting now
          </p>
          <p className="text-bank-muted mt-0.5 text-[10.5px]">forks to approved / rejected</p>
        </div>
      </div>

      <div className="border-bank-border flex flex-wrap items-center gap-2 border-t border-dashed pt-3.5">
        <Branch tone="dup" label="Merged as duplicate" value={duplicate} />
        <Branch tone="bad" label="Failed (any automated stage)" value={failed} />
        <Branch tone="good" label="Approved" value={approved} />
        <Branch tone="inert" label="Rejected" value={rejected} />
        <span className="text-bank-muted ml-auto text-[11px]">
          A human decision, tracked the same way as a pipeline stage.
        </span>
      </div>
    </div>
  );
}

function StageNode({ node, first }: { node: Node; first: boolean }) {
  const complete = node.total > 0 && node.done >= node.total;
  const active = node.done > 0 && node.done < node.total;
  return (
    <div className="relative flex-1 text-center">
      {!first && (
        <span
          className={cn(
            "absolute top-[5.5px] right-1/2 left-[-50%] h-[2px]",
            complete ? "bg-bank-good/55" : "bg-bank-border",
          )}
          aria-hidden="true"
        />
      )}
      <span
        className={cn(
          "relative z-10 mx-auto mb-2 block size-[15px] rounded-full border-[2.5px]",
          complete
            ? "border-bank-good bg-bank-good"
            : active
              ? "border-bank-pipe bg-bank-surface"
              : "border-bank-inert bg-bank-surface",
        )}
      />
      <p className="text-sm font-semibold">{node.label}</p>
      <p
        className={cn(
          "font-mono text-xs tabular-nums",
          active ? "text-bank-pipe-ink font-semibold" : "text-bank-muted",
        )}
      >
        {node.done}/{node.total}
      </p>
      {node.sub && <p className="text-bank-muted mt-0.5 text-[10.5px]">{node.sub}</p>}
    </div>
  );
}

const BRANCH_TONE = {
  dup: "bg-bank-dup-wash text-bank-dup-ink",
  bad: "bg-bank-bad-wash text-bank-bad-ink",
  good: "bg-bank-good-wash text-bank-good-ink",
  inert: "bg-bank-inert-wash text-bank-inert-ink",
} as const;

function Branch({ tone, label, value }: { tone: keyof typeof BRANCH_TONE; label: string; value: number }) {
  if (value === 0) return null;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs", BRANCH_TONE[tone])}>
      {label} <span className="font-mono font-semibold tabular-nums">{value}</span>
    </span>
  );
}
